import dayjs from 'dayjs';
import Highcharts from 'highcharts';
import { useMemo, useRef } from 'react';
import {
  HedgePlanBudgetTriggerModel,
  HedgePlanTermReportModel,
  HedgePlanTimeTriggerModel,
  HedgePlanTradeModel,
  HedgePlanValueTriggerModel,
} from '../../../../Generated/Commodity-Management-Api';
import StoneXChart from '../../../StoneX/StoneXChart';
import {
  HEDGE_KIND_COLOR_VARIABLES,
  HEDGE_KIND_ICONS,
  HEDGE_PLAN_TRIGGER_KINDS,
  readHedgePlanColor,
} from './HedgePlanTriggerStyles';
import { HEDGE_MARKER_SIZE, aggregateTrades, buildHedgeMarkerImage, findMarketPriceOnOrBefore } from './HedgePlanChartHelpers';
import { MatrixBand } from './HedgePlanMatrixBands';
import { readDecileColor } from '../DecileMatrixStyles';


// Distinct hues per period; the decile blue-to-orange severity scale is unsuitable here since its first few steps are all shades of blue.
const PERIOD_LINE_COLOR_VARIABLES = [
  '--hedge-plan-period-line-1', '--hedge-plan-period-line-2', '--hedge-plan-period-line-3', '--hedge-plan-period-line-4', '--hedge-plan-period-line-5',
  '--hedge-plan-period-line-6', '--hedge-plan-period-line-7', '--hedge-plan-period-line-8', '--hedge-plan-period-line-9', '--hedge-plan-period-line-10',
];
const TIME_TRIGGER_DAYS_BEFORE = 9;
const TIME_TRIGGER_DAYS_AFTER = 7;
// Empty space after the latest market price, so upcoming triggers aren't squeezed against the chart edge.
const X_AXIS_DAYS_AFTER_LAST_PRICE = 90;
const X_AXIS_DAYS_AFTER_LAST_TRIGGER = 14;
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

export interface HedgePlanChartEntry {
  periodKey: string;
  periodLabel: string;
  seasonEndDate?: string;
  matrix: HedgePlanTermReportModel;
  valueTriggers: HedgePlanValueTriggerModel[];
  budgetTriggers: HedgePlanBudgetTriggerModel[];
  timeTriggers: HedgePlanTimeTriggerModel[];
  trades: HedgePlanTradeModel[];
}

export interface SelectedMatrixBand {
  periodKey: string;
  periodLabel: string;
  band: MatrixBand;
}

interface HedgePlanCombinedChartProps {
  entries: HedgePlanChartEntry[];
  matrixCellSelections?: SelectedMatrixBand[];
  onHoveredPriceChange?: (periodKey: string, price: number | undefined) => void;
  /** Passed straight through to StoneXChart, which renders it with a native resize handle. */
  width?: string | number;
  showTimeTriggers?: boolean;
  /** Plot each trade at the market price on its trade date, ignoring its recorded price. */
  snapTradesToMarketPrice?: boolean;
  /** Label trades and time triggers with volumes in the report's unit instead of percents, where known. */
  showVolumes?: boolean;
}

// The later of 90 days past the last price and two weeks past the last trigger or trade, but not past the
// season's last delivery day (nothing after it applies) and never cutting off the price line.
function getXAxisMax(latestPriceTime: number | undefined, latestTriggerTime: number | undefined, seasonEndTime: number | undefined): number | undefined {
  const candidates = [
    latestPriceTime !== undefined ? latestPriceTime + X_AXIS_DAYS_AFTER_LAST_PRICE * MILLISECONDS_PER_DAY : undefined,
    latestTriggerTime !== undefined ? latestTriggerTime + X_AXIS_DAYS_AFTER_LAST_TRIGGER * MILLISECONDS_PER_DAY : undefined,
  ].filter((time): time is number => time !== undefined);

  if (candidates.length === 0) {
    return undefined;
  }

  const bufferedEnd = Math.max(...candidates);
  const cappedEnd = seasonEndTime !== undefined ? Math.min(bufferedEnd, seasonEndTime) : bufferedEnd;
  return latestPriceTime !== undefined ? Math.max(cappedEnd, latestPriceTime) : cappedEnd;
}

function amountLabel(percent: number, volume: number | null | undefined, showVolumes: boolean): string {
  return showVolumes && volume !== null && volume !== undefined
    ? Math.round(volume).toLocaleString('en-US')
    : `${Number(percent.toFixed(2))}%`;
}

// Every chart date is a calendar day, so plot it at local midnight. A bare "2026-10-07" would
// otherwise parse as UTC midnight and sit hours away from "2026-10-07T00:00:00", which parses as local.
function toChartTime(date: string): number {
  return dayjs(date.substring(0, 10)).valueOf();
}

export interface HedgePlanChartOptionsInput {
  entries: HedgePlanChartEntry[];
  matrixCellSelections?: SelectedMatrixBand[];
  showTimeTriggers?: boolean;
  snapTradesToMarketPrice?: boolean;
  showVolumes?: boolean;
  onHoveredPrice?: (periodKey: string, price: number | undefined) => void;
}

// The chart's Highcharts options, shared by the chart on the page and the PowerPoint export.
export function buildHedgePlanChartOptions(input: HedgePlanChartOptionsInput): Highcharts.Options {
  const {
    entries, matrixCellSelections = [], showTimeTriggers = true, snapTradesToMarketPrice = false, showVolumes = false, onHoveredPrice,
  } = input;
  const series: Highcharts.SeriesOptionsType[] = [];
  const timeTriggerBands: Highcharts.XAxisPlotBandsOptions[] = [];
  const matrixCellBands: Highcharts.YAxisPlotBandsOptions[] = [];

  const entryExecutions = entries.map((entry) =>
    aggregateTrades(entry.trades)
      .map((execution) => ({
        execution,
        price: snapTradesToMarketPrice
          ? findMarketPriceOnOrBefore(entry.matrix, execution.executionDate)
          : execution.price ?? findMarketPriceOnOrBefore(entry.matrix, execution.executionDate),
      }))
      .filter((item) => item.price !== undefined),
  );

  let latestTriggerTime: number | undefined;
  entries.forEach((entry, entryIndex) => {
    entry.timeTriggers
      .filter((trigger) => showTimeTriggers && !trigger.disabled)
      .forEach((trigger) => {
        const triggerTime = toChartTime(trigger.triggerDate);
        latestTriggerTime = latestTriggerTime === undefined ? triggerTime : Math.max(latestTriggerTime, triggerTime);
      });

    entryExecutions[entryIndex].forEach(({ execution }) => {
      const executionTime = toChartTime(execution.executionDate);
      latestTriggerTime = latestTriggerTime === undefined ? executionTime : Math.max(latestTriggerTime, executionTime);
    });
  });

  const priceTimes = entries.flatMap((entry) => entry.matrix.priceSeries
    .filter((point) => point.price !== null && point.price !== undefined && point.date)
    .map((point) => toChartTime(point.date!)));
  const latestPriceTime = priceTimes.length > 0 ? Math.max(...priceTimes) : undefined;
  const seasonEndTimes = entries
    .filter((entry) => entry.seasonEndDate)
    .map((entry) => toChartTime(entry.seasonEndDate!));
  const xAxisMax = getXAxisMax(latestPriceTime, latestTriggerTime, seasonEndTimes.length > 0 ? Math.max(...seasonEndTimes) : undefined);

  entries.forEach((entry, entryIndex) => {
    const color = readHedgePlanColor(PERIOD_LINE_COLOR_VARIABLES[entryIndex % PERIOD_LINE_COLOR_VARIABLES.length]);
    const priceData = entry.matrix.priceSeries
      .filter((point) => point.price !== null && point.price !== undefined && point.date)
      .map((point) => [toChartTime(point.date!), point.price as number] as [number, number])
      .sort((left, right) => left[0] - right[0]);
    const firstTime = priceData[0]?.[0];
    const lastTime = priceData[priceData.length - 1]?.[0];

    if (firstTime !== undefined && lastTime !== undefined) {
      entry.budgetTriggers
        .filter((trigger) => !trigger.disabled)
        .forEach((trigger, triggerIndex) => {
          series.push({
            // Stable ids make Highcharts match series by identity on update. Without them it
            // matches by array position and merges, so a series inherits leftover options from
            // whatever previously sat at its index — e.g. this dashStyle bleeding onto a price line.
            id: `${entry.periodKey}-budget-${triggerIndex}`,
            name: `${entry.periodLabel} Budget`,
            type: 'line',
            data: [[firstTime, trigger.price], [lastTime, trigger.price]],
            color,
            lineWidth: 1,
            dashStyle: 'Dash',
            marker: { enabled: false },
            showInLegend: false,
            enableMouseTracking: false,
            zIndex: 3,
          } as Highcharts.SeriesLineOptions);
        });
    }

    series.push({
      id: `${entry.periodKey}-price`,
      name: entry.periodLabel,
      type: 'line',
      data: priceData,
      color,
      lineWidth: 2,
      marker: { enabled: false },
      custom: { periodKey: entry.periodKey },
      point: {
        events: {
          mouseOver: function (this: Highcharts.Point) {
            onHoveredPrice?.(entry.periodKey, this.y as number);
          },
        },
      },
      events: {
        mouseOut: function () {
          onHoveredPrice?.(entry.periodKey, undefined);
        },
      },
      zIndex: 5,
    } as Highcharts.SeriesLineOptions);

    const executions = entryExecutions[entryIndex];

    HEDGE_PLAN_TRIGGER_KINDS.forEach((kind) => {
      const kindExecutions = executions.filter(({ execution }) => execution.kind === kind);
      if (kindExecutions.length === 0) {
        return;
      }

      const executionStyle = HEDGE_KIND_COLOR_VARIABLES[kind];
      const executionColor = readHedgePlanColor(executionStyle.line);
      series.push({
        id: `${entry.periodKey}-executions-${kind}`,
        name: `${entry.periodLabel} ${executionStyle.label}`,
        type: 'scatter',
        data: kindExecutions.map(({ execution, price }) => ({
          x: toChartTime(execution.executionDate),
          y: price as number,
          amountLabel: amountLabel(execution.executionPercent, execution.executionVolume, showVolumes),
          triggerLabel: execution.label,
        })),
        // Scatter points don't join the shared tooltip, and their default one prints the raw timestamp.
        tooltip: {
          headerFormat: '',
          pointFormat: '<span style="font-size: 0.8em">{point.x:%a, %b %e, %Y}</span><br/>'
            + '<b>{point.triggerLabel}</b>: {point.amountLabel} at ${point.y:.4f}',
        },
        color: executionColor,
        lineWidth: 0,
        marker: {
          enabled: true,
          symbol: buildHedgeMarkerImage(HEDGE_KIND_ICONS[kind], executionColor),
          width: HEDGE_MARKER_SIZE,
          height: HEDGE_MARKER_SIZE,
        },
        states: { hover: { lineWidthPlus: 0 } },
        showInLegend: false,
        zIndex: 7,
      } as Highcharts.SeriesScatterOptions);
    });


    matrixCellSelections
      .filter((selection) => selection.periodKey === entry.periodKey)
      .forEach((selection) => {
        const bandColor = readDecileColor(selection.band.percentile);
        matrixCellBands.push({
          from: selection.band.lowValue,
          to: selection.band.highValue,
          color: `${bandColor}33`,
          borderColor: bandColor,
          borderWidth: 1,
          label: {
            text: `${entry.periodLabel}: ${selection.band.label}`,
            style: { color: 'var(--font-color)', fontWeight: '600', fontSize: '11px' },
          },
        });
      });

    entry.timeTriggers
      .filter((trigger) => showTimeTriggers && !trigger.disabled)
      .forEach((trigger) => {
        const triggerTime = toChartTime(trigger.triggerDate);
        const targetVolume = entry.matrix.summary?.triggers
          .find((progress) => progress.triggerType === 'Time' && progress.triggerId === trigger.hedgePlanTimeTriggerId)
          ?.targetHedgeVolume;
        timeTriggerBands.push({
          from: triggerTime - TIME_TRIGGER_DAYS_BEFORE * MILLISECONDS_PER_DAY,
          to: triggerTime + TIME_TRIGGER_DAYS_AFTER * MILLISECONDS_PER_DAY,
          color: `${color}22`,
          borderColor: color,
          borderWidth: 1,
          label: {
            text: `${entry.periodLabel} ${amountLabel(trigger.targetHedgePercent, targetVolume, showVolumes)}`,
            rotation: 90,
            align: 'center',
            verticalAlign: 'top',
            y: 40,
            style: {
              width: 140,
              whiteSpace: 'nowrap',
              textOverflow: 'clip',
              fontWeight: '600',
              color: 'var(--gray4)',
            },
          },
        });
      });
  });

  return {
    chart: { type: 'line', animation: false },
    // Dates are parsed as local midnight, so format them in local time too. In UTC they land a few
    // hours past midnight, and the tooltip header switches to a weekday-and-time format.
    time: { useUTC: false },
    title: { text: undefined },
    credits: { enabled: false },
    xAxis: {
      type: 'datetime',
      units: [['month', [1, 2, 3, 4, 6]], ['year', [1]]],
      labels: { format: '{value:%b %Y}' },
      crosshair: true,
      plotBands: timeTriggerBands,
      max: xAxisMax,
    },
    yAxis: { title: { text: 'Price' }, labels: { format: '${value:.2f}' }, plotBands: matrixCellBands },
    plotOptions: {
      series: {
        animation: false,
        states: { inactive: { opacity: 1 }, hover: { halo: null } },
        marker: { states: { hover: { enabled: false } } },
      },
    },
    tooltip: {
      shared: true,
      xDateFormat: '%a, %b %e, %Y',
      valueDecimals: 4,
      valuePrefix: '$',
    },
    legend: { enabled: true },
    series,
  };
}

export default function HedgePlanCombinedChart(props: HedgePlanCombinedChartProps) {
  const {
    entries, matrixCellSelections = [], onHoveredPriceChange, width,
    showTimeTriggers = true, snapTradesToMarketPrice = false, showVolumes = false,
  } = props;
  const hoveredPriceHandler = useRef(onHoveredPriceChange);
  hoveredPriceHandler.current = onHoveredPriceChange;

  const options = useMemo<Highcharts.Options>(() => buildHedgePlanChartOptions({
    entries,
    matrixCellSelections,
    showTimeTriggers,
    snapTradesToMarketPrice,
    showVolumes,
    onHoveredPrice: (periodKey, price) => hoveredPriceHandler.current?.(periodKey, price),
  }), [entries, matrixCellSelections, showTimeTriggers, snapTradesToMarketPrice, showVolumes]);

  return <StoneXChart options={options} width={width} />;
}